import { UserOutlined } from '@ant-design/icons';
import { App, Button, Form, Input, Pagination, Spin, Table, Tag } from 'antd';
import Header from 'components/Header/Header';
import NavBar from 'components/NavBar';
import RightGif from 'components/RightGif';
import { SITE } from 'config/config';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { claimReward, claimWorldCupReward, historyPredictions } from 'service/base';
import 'styles/global.css';
import { twMerge } from 'tailwind-merge';

const HistoryPredictions = ({ isModalVote, setIsModalVote, IP, FP }) => {
  const { notification } = App.useApp();
  const [formHistory] = Form.useForm();
  const [account, setAccount] = useState('');
  const [dataPrediction, setDataPrediction] = useState([]);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [claimingMatchId, setClaimingMatchId] = useState(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    size: 'default',
    showSizeChanger: false,
    hideOnSinglePage: true
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: Array.isArray(dataPrediction) ? dataPrediction.length : 0
    }));
  }, [dataPrediction]);

  useEffect(() => {
    // Load saved account from localStorage
    const savedAccount = localStorage.getItem('prediction_account');
    if (savedAccount) {
      setAccount(savedAccount);
    }
  }, []);
  const loadHistory = async (accountValue) => {
    const accountInput = (accountValue ?? account).trim();
    if (!accountInput) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đăng nhập!'
      });
      return;
    }

    setIsLoadingSubmit(true);
    try {
      const res = await historyPredictions({
        account: accountInput,
        site: SITE,
        ip: IP,
        fp: FP
      });
      const rows = res?.data?.responseData || [];
      setDataPrediction(rows);
      if (!rows[0]) {
        notification.warning({
          message: 'Không có lịch sử',
          description: 'Không tìm thấy lịch sử bình chọn, Quý khách vui lòng kiểm tra lại!'
        });
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const onFinishHistoryForm = async () => {
    await loadHistory(account);
  };

  const isMatchPrediction = (record) =>
    Boolean(record?.matchId && record?.homeName && record?.awayName);

  const getClaimRowKey = (item) => item.matchId || item._id;

  const handleClaimReward = async (item) => {
    const isWorldCup = !isMatchPrediction(item);

    if (isWorldCup) {
      if (item.status_register !== 'apply' || !item._id || !item.predictType) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Dự đoán không hợp lệ để được nhận thưởng!'
        });
        return;
      }
    } else if (
      (item.status_register !== 'apply' && item.status_first_goal !== 'apply') ||
      !item.matchId
    ) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Dự đoán không hợp lệ để được nhận thưởng!'
      });
      return;
    }

    const accountValue = account.trim().toLowerCase();
    if (!accountValue) {
      notification.warning({
        message: 'Cảnh báo',
        description: 'Vui lòng nhập tên đăng nhập!'
      });
      return;
    }

    setClaimingMatchId(getClaimRowKey(item));
    try {
      const response = isWorldCup
        ? await claimWorldCupReward({
            id: item._id,
            account: accountValue,
            predictType: item.predictType
          })
        : await claimReward({
            account: accountValue,
            site: SITE,
            matchId: String(item.matchId)
          });

      if (response?.code === 0) {
        notification.success({
          message: 'Thành công',
          description: response?.text_mess || 'Nhận thưởng thành công!'
        });
        await loadHistory(accountValue);
      }
    } catch (error) {
      console.error('Error claiming reward:', error);
      notification.error({
        message: 'Nhận thưởng thất bại!',
        description: error.message || 'Vui lòng thử lại sau.'
      });
    } finally {
      setClaimingMatchId(null);
    }
  };

  const formatRewardPoint = (point) => {
    const n = Number(point);
    return Number.isFinite(n) && n > 0 ? n : '-';
  };

  const formatFirstGoalResult = (value) => {
    if (value === 'home') return 'Đội Nhà';
    if (value === 'away') return 'Đội Khách';
    return value || '-';
  };

  const onFinishHistoryFailed = () => {
    notification.warning({
      message: 'Cảnh báo',
      description: 'Vui lòng kiểm tra lại tài khoản !'
    });
  };

  const handleAccountChange = (event) => {
    const newAccount = event.target.value.trim();
    setAccount(newAccount.trim());
  };

  const historyRows = dataPrediction || [];
  const paginatedRows = historyRows.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const getMatchTitle = (record) =>
    record.type ? record.type : `${record.homeName} Vs ${record.awayName}`;

  const renderVoteSelection = (record) => {
    if (record.playerCode) {
      return <p className="mb-0">{record.playerName}</p>;
    }
    if (record.teamName) {
      return <p className="mb-0">{record.teamName}</p>;
    }
    return (
      <div className="jun88-history-vote">
        <p className="mb-0">
          <span className="inline-block rounded px-2 py-0.5 bg-black text-white font-medium">
            {record.homeScore}-{record.awayScore}
          </span>
        </p>
        <p className="mb-0">
          {record.firstGoal === 'home'
            ? record.homeName
            : record.firstGoal === 'away'
              ? record.awayName
              : 'Chưa xác định'}
        </p>
      </div>
    );
  };

  const renderResultTag = (record) => {
    const getTagColor = (status) => {
      if (status === 'apply') return '#00890a';
      if (status === 'deny') return '#ff0000';
      return '#ffffff';
    };

    const getTextColor = (status) => {
      if (status === 'apply' || status === 'deny') return '#ffffff';
      return '#000000';
    };

    return (
      <Tag
        style={{
          backgroundColor: getTagColor(record.status_register),
          color: getTextColor(record.status_register),
          border: record.status_register ? 'none' : '1px solid #d9d9d9',
          margin: 0
        }}
      >
        {record.match_result || record.resultText || '-'}
      </Tag>
    );
  };

  const renderClaimButton = (record) => {
    const isWorldCup = !isMatchPrediction(record);
    const isClaimable = isWorldCup
      ? record.status_register === 'apply'
      : record.status_register === 'apply' || record.status_first_goal === 'apply';
    const isClaiming = String(claimingMatchId) === String(getClaimRowKey(record));
    const isClaimed = isWorldCup
      ? record.status_register === 'accept'
      : record.status_register === 'accept' || record.status_first_goal === 'accept';

    return (
      <button
        type="button"
        onClick={() => handleClaimReward(record)}
        disabled={!isClaimable || isClaiming}
        className={`px-2 md:px-3 py-1.5 text-xs rounded-md md:rounded-lg font-semibold text-white border-0 whitespace-nowrap transition-opacity ${
          isClaimable
            ? 'bg-[#F49A25] cursor-pointer hover:opacity-95'
            : 'bg-[#929292] cursor-not-allowed'
        } disabled:opacity-70`}
      >
        {isClaiming ? 'Đang xử lý...' : isClaimed ? 'Đã nhận thưởng' : 'Nhận thưởng'}
      </button>
    );
  };

  const renderHistoryCard = (record, index) => (
    <div
      key={record?._id || `${record?.createdAt || 'row'}-${index}`}
      className="rounded-xl border border-[#d7e7f5] bg-white p-3 shadow-sm"
    >
      <p className={`mb-3 text-sm font-bold ${record.type ? 'text-[#26a9e0]' : 'text-[#333]'}`}>
        {getMatchTitle(record)}
      </p>

      <div className="space-y-2 text-xs">
        <div className="flex items-start justify-between gap-3">
          <span className="shrink-0 text-gray-500">Bình chọn</span>
          <div className="text-right">{renderVoteSelection(record)}</div>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500">Ngày dự đoán</span>
          <span>{record.createdAt ? dayjs(record.createdAt).format('DD/MM/YYYY') : '-'}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500">Kết quả</span>
          {renderResultTag(record)}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500">Đội ghi bàn trước</span>
          <span>{formatFirstGoalResult(record.first_goal_result)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500">VIP Level</span>
          <span>{record.vipLevel != null && record.vipLevel !== '' ? record.vipLevel : '-'}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-gray-500">Điểm thưởng</span>
          <span>{isMatchPrediction(record) ? formatRewardPoint(record.point) : '-'}</span>
        </div>
      </div>

      <div className="mt-3 flex justify-end border-t border-[#eef4f8] pt-3">
        {renderClaimButton(record)}
      </div>
    </div>
  );

  const columns = [
    {
      title: 'Trận đấu',
      width: '20%',
      align: 'center',
      render: (_, record) =>
        record.type ? (
          <span style={{ color: '#26a9e0' }}>{record.type}</span>
        ) : (
          getMatchTitle(record)
        )
    },
    {
      title: 'Bình chọn',
      width: '20%',
      align: 'center',
      render: (_, record) => renderVoteSelection(record)
    },
    {
      title: 'Ngày dự đoán',
      width: '20%',
      align: 'center',
      dataIndex: 'createdAt',
      render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-')
    },
    {
      title: 'Kết quả',
      width: '20%',
      align: 'center',
      render: (_, record) => renderResultTag(record)
    },
    {
      title: 'Đội ghi bàn trước',
      width: '12%',
      align: 'center',
      render: (_, record) => formatFirstGoalResult(record.first_goal_result)
    },
    {
      title: 'VIP Level',
      width: '8%',
      align: 'center',
      render: (_, record) =>
        record.vipLevel != null && record.vipLevel !== '' ? record.vipLevel : '-'
    },
    {
      title: 'Điểm thưởng',
      width: '8%',
      align: 'center',
      render: (_, record) => (record.point ? formatRewardPoint(record.point) : '-')
    },
    {
      title: 'Nhận thưởng',
      width: '12%',
      align: 'center',
      render: (_, record) => renderClaimButton(record)
    }
  ];
  return (
    <div className={twMerge(SITE, 'w-full h-full')}>
      <img
        src="bg.png"
        alt="Background"
        className="w-full h-full fixed right-0 bottom-0 object-cover object-center"
      />
      <RightGif />
      <div className="container relative z-10 pb-[110px] md:pb-[32px] px-0 md:px-2">
        <Header />
        <div className="mx-auto max-w-[1280px] mt-2 bg-[#FFFFFF99] px-2 py-4 md:p-4 w-full md:pb-8 md:pt-4 rounded-2xl backdrop-blur-[48.53px]">
          <Form
            name="formHistoryMatch"
            onFinish={onFinishHistoryForm}
            onFinishFailed={onFinishHistoryFailed}
            className="relative"
            autoComplete="off"
            form={formHistory}
            initialValues={{ account: account }}
          >
            <h1 className="text-primary text-xl md:text-2xl text-center font-bold">
              LỊCH SỬ DỰ ĐOÁN
            </h1>
            <div className="flex md:justify-center justify-between gap-2 md:py-4 py-2 w-full relative">
              <Form.Item
                name="account"
                className="mb-0 min-w-[30%] md:min-h-[38px] w-full max-w-[360px]"
              >
                <Input
                  className="min-h-[38px] w-full"
                  placeholder="Nhập tên đăng nhập ..."
                  maxLength={30}
                  value={account}
                  onChange={handleAccountChange}
                  prefix={<UserOutlined />}
                  rules={[
                    {
                      required: true,
                      message: 'Vui lòng điền tên đăng nhập!'
                    }
                  ]}
                />
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isLoadingSubmit}
                  className="bg-score bg-check-account text-white font-medium min-h-[32px] absolute right-3 top-[50%] translate-y-[-50%] z-30"
                >
                  Xác Nhận
                </Button>
              </Form.Item>
            </div>
          </Form>
          <div className="list-history">
            {/* {dataPrediction?.championPrediction != null && (
              <div className="mb-4 text-center">
                <span className="font-medium">Dự đoán vô địch mùa giải: </span>
                <span className="font-bold">{dataPrediction?.championPrediction?.teamName}</span>
              </div>
            )}
            {dataPrediction?.topScorerPrediction != null && (
              <div className="mb-4 text-center">
                <span className="font-medium">Dự đoán vua phá lưới: </span>
                <span className="font-bold">{dataPrediction?.topScorerPrediction?.playerName}</span>
              </div>
            )} */}
            <div className="md:hidden">
              <Spin spinning={isLoadingSubmit}>
                {paginatedRows.length > 0 ? (
                  <div className="space-y-3">{paginatedRows.map(renderHistoryCard)}</div>
                ) : (
                  <div className="py-8 text-center text-sm text-gray-500">Không có dữ liệu</div>
                )}
                {pagination.total > pagination.pageSize && (
                  <div className="mt-4 flex justify-center">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      showSizeChanger={false}
                      onChange={(page, pageSize) =>
                        setPagination((prev) => ({ ...prev, current: page, pageSize }))
                      }
                    />
                  </div>
                )}
              </Spin>
            </div>

            <Table
              className="jun88-history-table hidden w-full md:table"
              size="small"
              bordered={false}
              columns={columns}
              dataSource={historyRows}
              loading={isLoadingSubmit}
              pagination={{
                ...pagination,
                onChange: (page, pageSize) =>
                  setPagination((prev) => ({ ...prev, current: page, pageSize }))
              }}
              rowKey={(record, index) => record?._id || `${record?.createdAt || 'row'}-${index}`}
              tableLayout="fixed"
              locale={{ emptyText: 'Không có dữ liệu' }}
            />
          </div>
        </div>
      </div>
      <NavBar />
    </div>
  );
};

export default HistoryPredictions;
